import React from 'react';
import type { ButtonHTMLAttributes } from 'react';
import type { ButtonProps } from './types';
import type { AllIconType } from '../../util/types';
import { ButtonStyled } from './Button.styled';
import MaterialIcon from '../icon/Icon.material';
import { getIconComponent } from '../../util/reactFunc';
import { BUTTON_TOGGLE, BUTTON_WRAPPER, ICON_WRAPPER } from '../../util/data-id';
import { GLOBAL_ICONS } from '../../util/vars';
import classNames from 'classnames';
import Loader from '../loader/Loader';

type Ref = HTMLButtonElement;
type HTMLProps = Omit<ButtonHTMLAttributes<Ref>, 'color'>;
interface Props extends ButtonProps, HTMLProps {
  /**
   * Sets border-radius as pill.
   */
  isPill?: boolean;
  /**
   * Sets the button to bold
   */
  isBold?: boolean;
  /**
   * Shows loading spinner
   */
  isLoading?: boolean;
  /**
   * Adds an icon to the left of the button.
   */
  icon?: AllIconType;
  /**
   * Styles button as a link.
   */
  asLink?: boolean;
  /**
   * Wrap the label
   */
  inlineText?: boolean;
  /**
   * Button content.
   */
  children: React.ReactNode;
}

const Button = React.forwardRef<Ref, Props>(
  (
    {
      color = 'primary',
      size = 'md',
      isOutline = false,
      isPill = false,
      isBold = false,
      isLoading = false,
      icon,
      toggle = null,
      highlight = false,
      children,
      align = 'center',
      asLink = false,
      inlineText = false,
      ...other
    }: Props,
    ref,
  ): JSX.Element => {
    return (
      <ButtonStyled
        ref={ref}
        type={'type' in other ? other?.type : 'button'}
        data-id={BUTTON_WRAPPER}
        aria-pressed={toggle as boolean}
        $color={color}
        $highlight={highlight}
        $size={size}
        $isOutline={isOutline}
        $isPill={isPill}
        $isBold={isBold}
        $isToggle={null !== toggle}
        $hasIcon={!!icon}
        $align={align}
        $asLink={asLink}
        {...(other as HTMLProps)}
      >
        <div className={`w-full flex items-center justify-center ${classNames( { 'whitespace-nowrap': inlineText })}`}>
          {isLoading
            ? <Loader disableLabel smallSize noMargin data-id={ICON_WRAPPER} />
            : (icon && getIconComponent(icon, size, color))
          }
          {children}
          {null !== toggle && (
            <MaterialIcon icon={GLOBAL_ICONS[toggle ? 'toggle_on' : 'toggle_off']} className={BUTTON_TOGGLE} />
          )}
        </div>
      </ButtonStyled>
    );
  },
);

Button.displayName = 'Button';

export default Button;
